import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

export interface LogoLinksSearchStyles {
    container: InjectableCss;
    logoWrapper: InjectableCss;
    searchWrapper: InjectableCss;
    searchMenuWrapper: InjectableCss;
    linkListHidden: HiddenProps;
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            max-width: 100%;
            margin: 0 auto;
            background: #fff;
            padding: 0 15px;
            max-width: 1280px;
        `,
    },
    logoWrapper: {
        css: css`
            text-align: left;
            padding: 1px;
            margin: 0px;
            width: 20%;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            text-align: center;
                            > div[class^="Wrapper"] {
                                > div[class^="GridWrapper"] {
                                    > div[class^="GridOffset"] {
                                        > div[class^="GridItemStyle"] {
                                            flex-basis: 100%;
                                            max-width: 100%;
                                        }
                                    }
                                }
                            }
                        `,
                        css`
                            width: 100%;
                            text-align: center;
                            > div[class^="Wrapper"] {
                                > div[class^="GridWrapper"] {
                                    > div[class^="GridOffset"] {
                                        > div[class^="GridItemStyle"] {
                                            flex-basis: 100%;
                                            max-width: 100%;
                                        }
                                    }
                                }
                            }
                        `,
                        css`
                            flex-grow: 1;
                        `,
                        null,
                        null,
                    ],
                    "minMax",
                );
            }}
            > div[class^="Wrapper"] {
                > div[class^="GridWrapper"] {
                    > div[class^="GridOffset"] {
                        > div:first-child[class^="GridItemStyle"] {
                            > div[class^="Wrapper"] {
                                > div {
                                    ${({ theme }) => {
                                        return breakpointMediaQueries(
                                            theme,
                                            [
                                                css`
                                                    max-width: 100%;
                                                `,
                                                css`
                                                    max-width: 100%;
                                                `,
                                                null,
                                                null,
                                            ],
                                            "minMax",
                                        );
                                    }}
                                }
                            }
                        }
                    }
                }
            }
            a[class^="StyledButton"] {
                div {
                    display: flex;
                }
            }
            img {
                height: 78px;
                width: auto;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                height: 95px;
                                margin: 10px;
                            `,
                            css`
                                height: 95px;
                                margin: 10px;
                            `,
                            css`
                                height: 42px;
                                width: auto;
                                margin: 0;
                            `,
                            null,
                            null,
                        ],
                        "minMax",
                    );
                }}
            }
        `,
    },
    searchWrapper: {
        css: css`
            width: 40% !important;
            padding-left: 55px;
            display: flex;
            justify-content: center;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                        css`
                            width: 45% !important;
                        `,
                        css`
                            width: 40% !important;
                        `,
                    ],
                    "minMax",
                )}
            > div[class^="Wrapper"] {
                div[class^="OverflowWrapper"] {
                    max-width: 580px;
                    width: 580px;
                    float: right;
                }
                [class^="FormFieldStyle"] {
                    width: 100%;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                css`
                                    width: 250px;
                                `,
                                null,
                            ],
                            "minMax",
                        )}
                    [class^="FormInputWrapper"] {
                        input {
                            font-size: 13px;
                            background: rgb(241, 241, 241);
                            border: 1px solid rgb(204, 204, 204);
                        }
                    }
                }
            }
            input[type="text"] {
                & + button {
                    background: rgb(204, 204, 204);
                    height: 40px;
                }
            }
            svg {
                color: #555555;
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            text-align: right;
            padding: 0px;
            width: 10%;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            > div[class^="HiddenStyle"] > div[class^="Wrapper"] > div[class^="sc-"] > a > p {
                color: #000;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                        css`
                            flex-grow: 8;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    linkListHidden: {
        below: "lg",
    },
};

const LogoLinksSearch: React.FC<PageProps> = ({ id }) => (
    <StyledWrapper {...logoLinksSearchStyles.container}>
        <StyledWrapper {...logoLinksSearchStyles.logoWrapper}>
            <Zone contentId={id} zoneName="Logo" />
        </StyledWrapper>
        <StyledWrapper {...logoLinksSearchStyles.searchWrapper}>
            <Zone contentId={id} zoneName="Search" fixed />
        </StyledWrapper>
        <StyledWrapper {...logoLinksSearchStyles.searchMenuWrapper}>
            <Hidden {...logoLinksSearchStyles.linkListHidden}>
                <Zone contentId={id} zoneName="LinkList" />
            </Hidden>
        </StyledWrapper>
    </StyledWrapper>
);

const logoLinksSearch: WidgetModule = {
    component: LogoLinksSearch,
    definition: {
        group: "Common",
        icon: "magnifying-glass",
        isSystem: true,
    },
};

export default logoLinksSearch;
